import { useEffect } from 'react'

interface PageMeta {
  title: string
  description?: string
  imageUrl?: string
  // When set, emits the iOS Smart App Banner meta tag so Safari shows a
  // native "OPEN" button that uses the universal link.
  iosAppId?: string
}

type Restore = () => void

// Sets a meta tag and returns a function that undoes the change. If the tag
// existed before (e.g. defined statically in index.html), restore its original
// content; otherwise remove the element we created.
function setMetaTag(attr: string, key: string, content: string): Restore {
  const existing = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (existing) {
    const original = existing.getAttribute('content')
    existing.setAttribute('content', content)
    return () => {
      if (original === null) existing.removeAttribute('content')
      else existing.setAttribute('content', original)
    }
  }
  const created = document.createElement('meta')
  created.setAttribute(attr, key)
  created.setAttribute('content', content)
  document.head.appendChild(created)
  return () => created.remove()
}

export function usePageMeta(meta: PageMeta | null) {
  useEffect(() => {
    if (!meta) return

    const originalTitle = document.title
    document.title = meta.title

    const url = window.location.href
    const imageUrl = meta.imageUrl || `${window.location.origin}/app-icon.png`

    const restores: Restore[] = []
    restores.push(setMetaTag('property', 'og:title', meta.title))
    restores.push(setMetaTag('property', 'og:url', url))
    restores.push(setMetaTag('property', 'og:image', imageUrl))
    if (meta.description) restores.push(setMetaTag('property', 'og:description', meta.description))

    restores.push(setMetaTag('name', 'twitter:title', meta.title))
    restores.push(setMetaTag('name', 'twitter:image', imageUrl))
    if (meta.description) restores.push(setMetaTag('name', 'twitter:description', meta.description))

    if (meta.iosAppId) {
      restores.push(setMetaTag('name', 'apple-itunes-app', `app-id=${meta.iosAppId}, app-argument=${url}`))
    }

    return () => {
      document.title = originalTitle
      for (const r of restores) r()
    }
  }, [meta?.title, meta?.description, meta?.imageUrl, meta?.iosAppId])
}
